import type { RectangleFormat, RectangleProperty } from '../elements/shapes/rectangle/rectangle.model';
import type { SolvedRectangle } from './solver.model';

/**
 * Auto-detect rectangle format from available scalar keys.
 */
export function detectRectangleFormat(values: Partial<Record<RectangleProperty, number>>): RectangleFormat {
  const hasW = values.w !== undefined;
  const hasH = values.h !== undefined;
  const hasD = values.d !== undefined;

  if (hasW && hasH) {
    return 'WH';
  }
  if (hasW && hasD) {
    return 'WD';
  }
  if (hasH && hasD) {
    return 'HD';
  }

  throw new Error(
    `Cannot detect rectangle format: need at least 2 of {w, h, d}. Got: ${Object.keys(values).join(', ')}`,
  );
}

/**
 * Solve a rectangle given numeric values. Format is auto-detected if not provided.
 * Returns all properties and vertices in canonical placement.
 *
 * Canonical placement: A at origin, width along +x, height along +y.
 * A = (0,0), B = (w,0), C = (w,h), D = (0,h) (counterclockwise)
 */
export function solveRectangle(
  values: Partial<Record<RectangleProperty, number>>,
  format?: RectangleFormat,
): SolvedRectangle {
  const resolvedFormat = format ?? detectRectangleFormat(values);

  let w: number, h: number, d: number;

  switch (resolvedFormat) {
    case 'WH':
      w = values.w!;
      h = values.h!;
      if (w === undefined || h === undefined) {
        throw new Error('WH format requires w and h');
      }
      d = Math.sqrt(w * w + h * h);
      break;

    case 'WD':
      w = values.w!;
      d = values.d!;
      if (w === undefined || d === undefined) {
        throw new Error('WD format requires w and d');
      }
      if (d <= w) {
        throw new Error('Diagonal must be greater than width');
      }
      h = Math.sqrt(d * d - w * w);
      break;

    case 'HD':
      h = values.h!;
      d = values.d!;
      if (h === undefined || d === undefined) {
        throw new Error('HD format requires h and d');
      }
      if (d <= h) {
        throw new Error('Diagonal must be greater than height');
      }
      w = Math.sqrt(d * d - h * h);
      break;

    default:
      throw new Error(`Unknown rectangle format: ${resolvedFormat as string}`);
  }

  if (w <= 0 || h <= 0) {
    throw new Error(`Width and height must be positive: w=${w}, h=${h}`);
  }

  return {
    vertices: {
      A: [0, 0],
      B: [w, 0],
      C: [w, h],
      D: [0, h],
    },
    w,
    h,
    d,
    area: w * h,
    perimeter: 2 * (w + h),
  };
}
